import { Header } from '@/components/layout/Header';
import { StatCard } from '@/components/dashboard/StatCard';
import { RevenueChart } from '@/components/dashboard/RevenueChart';
import { ProjectStatusChart } from '@/components/dashboard/ProjectStatusChart';
import { TimeTrackingChart } from '@/components/dashboard/TimeTrackingChart';
import { ClientAcquisitionChart } from '@/components/dashboard/ClientAcquisitionChart';
import { QuickActions } from '@/components/dashboard/QuickActions';
import { ActivityFeed } from '@/components/dashboard/ActivityFeed';
import { Users, FolderKanban, DollarSign, Clock } from 'lucide-react';
import { mockDashboardData } from '@/lib/mock-data';
import { useQuery } from '@tanstack/react-query';
import { LoadingSpinner } from '@/components/LoadingSpinner';

export default function Dashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: async () => {
      await new Promise((resolve) => setTimeout(resolve, 500));
      return mockDashboardData;
    },
  });

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <Header title="Dashboard" />
        <main className="flex-1 overflow-auto p-6 flex items-center justify-center">
          <LoadingSpinner />
        </main>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col h-full">
        <Header title="Dashboard" />
        <main className="flex-1 overflow-auto p-6">
          <div className="text-center text-gray-500">Failed to load dashboard data</div>
        </main>
      </div>
    );
  }

  const { stats, charts, activities } = data;

  return (
    <div className="flex flex-col h-full">
      <Header title="Dashboard" />

      <main className="flex-1 overflow-auto p-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            label="Total Clients"
            value={stats.totalClients.value}
            change={{
              value: stats.totalClients.change,
              isPositive: stats.totalClients.isPositive,
            }}
            icon={Users}
            iconBgColor="bg-blue-100"
          />
          <StatCard
            label="Active Projects"
            value={stats.activeProjects.value}
            change={{
              value: stats.activeProjects.change,
              isPositive: stats.activeProjects.isPositive,
            }}
            icon={FolderKanban}
            iconBgColor="bg-green-100"
          />
          <StatCard
            label="Revenue This Month"
            value={`$${stats.revenueThisMonth.value.toLocaleString()}`}
            change={{
              value: stats.revenueThisMonth.change,
              isPositive: stats.revenueThisMonth.isPositive,
            }}
            icon={DollarSign}
            iconBgColor="bg-purple-100"
          />
          <StatCard
            label="Hours Logged"
            value={stats.hoursLogged.value}
            change={{
              value: stats.hoursLogged.change,
              isPositive: stats.hoursLogged.isPositive,
            }}
            icon={Clock}
            iconBgColor="bg-orange-100"
          />
        </div>

        {/* Quick Actions */}
        <div className="mb-6">
          <QuickActions />
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <RevenueChart data={charts.revenue} />
          <ProjectStatusChart data={charts.projectStatus} />
          <TimeTrackingChart data={charts.timeTracking} />
          <ClientAcquisitionChart data={charts.clientAcquisition} />
        </div>

        {/* Activity Feed */}
        <div className="max-w-4xl">
          <ActivityFeed activities={activities} />
        </div>
      </main>
    </div>
  );
}
